import { useState, useEffect, useRef } from "react";
import compassIcon from '../assets/compass-icon.png';

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// ENRICH-AUTO-1: how often an open project re-checks pending enrichment (server polls FullEnrich every 60s).
export const ENRICH_STATUS_POLL_MS = 20000;


export default function ProjectView({ project, onBack }) {
  const [projectData, setProjectData] = useState(project);
  const [toast, setToast] = useState(null);
  const [selectedExperts, setSelectedExperts] = useState([]);
  const [activeTab, setActiveTab] = useState("experts");
  const [projectNotes, setProjectNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesUnreadCount, setNotesUnreadCount] = useState(0);
  const [settings, setSettings] = useState({ follow_up_delay_days: '3', linkedin_batch_delay_minutes: '5' });
  const [showSettings, setShowSettings] = useState(false);
  const [projectDetails, setProjectDetails] = useState({ name: '', client: '', description: '', priority: 'medium', pitch_template: '', subject_line: '', industry: '', topic: '', campaign_id: '', ticker: '', auto_followups: 0, follow_up_message: '', linkedin_followup_template: '' });
  const [enriching, setEnriching] = useState(false);
  // ENRICH-AUTO-1 (D-48): experts still waiting on FullEnrich for this project.
  // While > 0 the page re-checks every ENRICH_STATUS_POLL_MS and refetches when it
  // reaches 0; the server also notifies the user who clicked, via App.js.
  const [enrichPending, setEnrichPending] = useState(0);
  const [sendingPitch, setSendingPitch] = useState(false);
  const [sendingFollowup, setSendingFollowup] = useState(false);
  const [lastLinkedinOutreachTime, setLastLinkedinOutreachTime] = useState(0);
  const [editingEmailId, setEditingEmailId] = useState(null);
  const [showSalesNav, setShowSalesNav] = useState(false);
  const [salesNavUrl, setSalesNavUrl] = useState('');
  const [salesNavImporting, setSalesNavImporting] = useState(false);
  const salesNavPollRef = useRef(null);
  const [editingExpert, setEditingExpert] = useState(null);
  const [expertEditForm, setExpertEditForm] = useState({ name: '', title: '', location: '', email: '' });
  const [showAddExpert, setShowAddExpert] = useState(false);
  const [addExpertForm, setAddExpertForm] = useState({ name: '', title: '', location: '', email: '', sales_nav_url: '' });
  const [sources, setSources] = useState([]);
  const [activeSourceId, setActiveSourceId] = useState(null);
  const [openMenuExpertId, setOpenMenuExpertId] = useState(null);
  const [enrichCreditBalance, setEnrichCreditBalance] = useState(null);
  const [enrichCreditPrice, setEnrichCreditPrice] = useState(12);
  const [enrichCreditsShort, setEnrichCreditsShort] = useState(false);

  useEffect(() => {
    fetchEnrichCredits();
    const onCreditsChanged = () => fetchEnrichCredits();
    const onFocus = () => fetchEnrichCredits();
    window.addEventListener('rasp:credits-changed', onCreditsChanged);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('rasp:credits-changed', onCreditsChanged);
      window.removeEventListener('focus', onFocus);
    };
  }, []); // eslint-disable-line

  // ENRICH-AUTO-1: poll pending enrichment for this project; refetch when it finishes.
  const fetchEnrichStatus = async () => {
    try {
      const res = await fetch(`${API}/projects/${project.id}/enrich/status`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } });
      if (!res.ok) return null;
      const d = await res.json();
      const n = Number(d.pending_experts || 0);
      setEnrichPending(n);
      return n;
    } catch (e) { return null; }
  };
  useEffect(() => {
    fetchEnrichStatus();
    const onEnrichFinished = (ev) => {
      if (ev && ev.detail && ev.detail.project_id && ev.detail.project_id !== project.id) return;
      setEnrichPending(0);
      fetchProject();
      fetchEnrichCredits();
    };
    window.addEventListener('rasp:enrich-finished', onEnrichFinished);
    return () => window.removeEventListener('rasp:enrich-finished', onEnrichFinished);
  }, [project.id]); // eslint-disable-line
  useEffect(() => {
    if (enrichPending <= 0) return undefined;
    const t = setInterval(async () => {
      const n = await fetchEnrichStatus();
      if (n === 0) {
        fetchProject();
        window.dispatchEvent(new Event('rasp:credits-changed'));
        showToast('Enrichment complete — emails added to this project.');
      }
    }, ENRICH_STATUS_POLL_MS);
    return () => clearInterval(t);
  }, [enrichPending > 0, project.id]); // eslint-disable-line

  useEffect(() => {
    if (project?.id) fetchProject();
    fetchSettings();
    if (project?.id) fetchSources();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (project?.id && activeSourceId !== null) fetchProject();
  }, [activeSourceId]); // eslint-disable-line

  useEffect(() => {
    if (!projectData?.id) return;
    fetchNotesUnreadCount();
  }, [projectData?.id]); // eslint-disable-line

  useEffect(() => {
    if (activeTab !== 'notes' || !projectData?.id) return;
    const user = JSON.parse(localStorage.getItem('expertnet_user') || '{}');
    setNotesLoading(true);
    fetch(`${API}/projects/${projectData.id}/notes`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } })
      .then(r => r.json())
      .then(data => {
        setProjectNotes(data);
        setNotesLoading(false);
        if (user.id) {
          fetch(`${API}/projects/${projectData.id}/notes/mark-read`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
            body: JSON.stringify({ user_id: user.id }),
          }).then(() => setNotesUnreadCount(0)).catch(() => {});
        }
      })
      .catch(() => setNotesLoading(false));
  }, [activeTab, projectData?.id]); // eslint-disable-line

  const fetchNotesUnreadCount = async () => {
    const user = JSON.parse(localStorage.getItem('expertnet_user') || '{}');
    if (!user.id || !projectData?.id) return;
    try {
      const res = await fetch(`${API}/projects/${projectData.id}/notes/unread-count?user_id=${user.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } });
      if (res.ok) {
        const data = await res.json();
        setNotesUnreadCount(data.count || 0);
      }
    } catch (e) {}
  };

  const fetchProject = async () => {
    try {
      const url = activeSourceId !== null
        ? `${API}/projects/${project.id}?source_id=${activeSourceId}`
        : `${API}/projects/${project.id}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } });
      if (res.ok) {
        const data = await res.json();
        setProjectData(data);
        setProjectDetails({
          name: data.name || '',
          client: data.client || '',
          description: data.description || '',
          priority: data.priority || 'medium',
          pitch_template: data.pitch_template || '',
          subject_line: data.subject_line || '',
          industry: data.industry || '',
          topic: data.topic || '',
          campaign_id: data.campaign_id || '',
          ticker: data.ticker || '',
          auto_followups: data.auto_followups || 0,
          follow_up_message: data.follow_up_message || '',
          linkedin_followup_template: data.linkedin_followup_template || '',
        });
      }
    } catch (e) { console.error(e); }
  };

  const fetchSources = async () => {
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/projects/${project.id}/sources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSources(data.sources || []);
        if ((data.sources || []).length > 0 && activeSourceId === null) {
          setActiveSourceId(data.sources[0].id);
        }
      }
    } catch (e) { console.error(e); }
  };

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/settings`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } });
      if (res.ok) setSettings(await res.json());
    } catch (e) { console.error(e); }
  };

  const saveSettings = async () => {
    try {
      await fetch(`${API}/projects/${projectData.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify(projectDetails),
      });

      await fetch(`${API}/settings/follow_up_delay_days`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ value: settings.follow_up_delay_days }),
      });
      await fetch(`${API}/settings/linkedin_batch_delay_minutes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ value: settings.linkedin_batch_delay_minutes }),
      });
      showToast("Settings saved!");
      setShowSettings(false);
      fetchProject();
    } catch (e) { showToast("Save failed", "error"); }
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const sendToScheduling = async (expert, kind) => {
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/scheduling`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectData.id, expert_id: expert.id, kind }),
      });
      if (res.ok) {
        const data = await res.json();
        showToast(data.added === false ? `Already in ${kind}` : `Added to ${kind}`);
      }
    } catch (e) { console.error(e); }
    setOpenMenuExpertId(null);
  };

  const updateStatus = async (expertId, status) => {
    await fetch(`${API}/experts/${expertId}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
      body: JSON.stringify({ status }),
    });
    fetchProject();
  };

  const startSalesNavImport = async () => {
    if (!salesNavUrl.trim()) return;
    setShowSalesNav(false);
    setSalesNavImporting(true);
    try {
      await fetch(`${API}/projects/${projectData.id}/import-salesnav`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ list_url: salesNavUrl }),
      });
      salesNavPollRef.current = setInterval(async () => {
        try {
          const statusUrl = activeSourceId !== null
              ? `${API}/projects/${projectData.id}/import-salesnav/status?source_id=${activeSourceId}`
              : `${API}/projects/${projectData.id}/import-salesnav/status`;
          const res = await fetch(statusUrl, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } });
          const data = await res.json();
          if (data.status === 'finished') {
            clearInterval(salesNavPollRef.current);
            setSalesNavImporting(false);
            setSalesNavUrl('');
            showToast(`${data.imported} experts imported`);
            fetchProject();
          } else if (data.status === 'error') {
            clearInterval(salesNavPollRef.current);
            setSalesNavImporting(false);
            showToast(data.message || 'Import failed', 'error');
          }
        } catch (e) {
          console.error('Failed to check import status', e);
        }
      }, 10000);
    } catch (e) {
      setSalesNavImporting(false);
      showToast('Failed to start import', 'error');
    }
  };

  const saveExpertEdit = async () => {
    try {
      await fetch(`${API}/experts/${editingExpert.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify(expertEditForm),
      });
      setEditingExpert(null);
      fetchProject();
    } catch (e) { showToast('Failed to save', 'error'); }
  };

  const addExpertManually = async () => {
    if (!addExpertForm.name.trim()) { showToast('Name is required', 'error'); return; }
    try {
      const res = await fetch(`${API}/projects/${projectData.id}/experts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify(addExpertForm),
      });
      if (res.ok) {
        showToast('Expert added!');
        setShowAddExpert(false);
        setAddExpertForm({ name: '', title: '', location: '', email: '', sales_nav_url: '' });
        fetchProject();
      } else {
        const data = await res.json();
        showToast(data.error || 'Failed to add expert', 'error');
      }
    } catch (e) { showToast('Failed to add expert', 'error'); }
  };

  const saveEmail = async (expertId, email) => {
    if (!email) { setEditingEmailId(null); return; }
    setEditingEmailId(null);
    try {
      await fetch(`${API}/experts/${expertId}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ email }),
      });
      fetchProject();
    } catch (e) { showToast("Failed to update email", "error"); }
  };

  const fetchEnrichCredits = async () => {
    try {
      const res = await fetch(`${API}/credits/me`, { headers: { Authorization: `Bearer ${localStorage.getItem('expertnet_token')}` } });
      if (res.ok) {
        const data = await res.json();
        setEnrichCreditBalance(data.balance ?? null);
        setEnrichCreditPrice(data.credits_per_enrichment ?? 12);
        setEnrichCreditsShort(false);
      }
    } catch (e) {}
  };

  const enrichAll = async () => {
    setEnriching(true);
    try {
      const res = await fetch(`${API}/projects/${project.id}/enrich`, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem('expertnet_token')}` }, body: JSON.stringify({ expert_ids: selectedExperts }) });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 402) { setEnrichCreditsShort(true); }
        showToast(data.error || data.message || "Enrichment failed", "error");
        return;
      }
      window.dispatchEvent(new Event('rasp:credits-changed'));
      setEnrichCreditsShort(false);
      // ENRICH-AUTO-1: results arrive on their own; start watching for them.
      showToast(data.message || "Enrichment started — we'll let you know when it's finished.");
      setEnrichPending(Number(data.submitted || 0) || 1);
      fetchEnrichStatus();
    } catch (e) { showToast("Enrichment failed", "error"); }
    finally { setEnriching(false); }
  };

  const sendEmails = async () => {
    setSendingPitch(true);
    try {
      const res = await fetch(`${API}/projects/${project.id}/send-emails`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ expert_ids: selectedExperts }),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || data.message || "Send failed", "error");
        return;
      }
      const _skipped = (data.skipped_already_emailed || 0) + (data.skipped_in_progress || 0);
      if (_skipped > 0) {
        showToast(`${_skipped} skipped — already emailed or being sent`);
      } else {
        showToast(data.message || "Emails sent");
      }
      fetchProject();
    } catch (e) { showToast("Send failed", "error"); }
    finally { setSendingPitch(false); }
  };

  const sendLinkedInFollowups = async () => {
    setSendingFollowup(true);
    showToast("Sending follow-ups...");
    try {
      const res = await fetch(`${API}/projects/${project.id}/send-email-followups`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ expert_ids: selectedExperts }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || "Follow-up failed", "error"); return; }
      const _fuSkipped = (data.skipped_already_contacted || 0) + (data.skipped_in_progress || 0);
      if (_fuSkipped > 0) {
        showToast(`${_fuSkipped} skipped — already contacted or being sent`);
      } else {
        showToast(data.message || "Follow-ups sent");
      }
      fetchProject();
    } catch (e) { showToast("Send failed", "error"); }
    finally { setSendingFollowup(false); }
  };

  const logLinkedinOutreach = async () => {
    const now = Date.now();
    if (lastLinkedinOutreachTime && (now - lastLinkedinOutreachTime) < 30000) {
      const secs = Math.round((now - lastLinkedinOutreachTime) / 1000);
      if (!window.confirm(`You logged LinkedIn outreach ${secs} second(s) ago. Log again for the ${selectedExperts.length} selected expert(s)?`)) {
        return;
      }
    }
    try {
      const res = await fetch(`${API}/projects/${project.id}/log-linkedin-outreach`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem('expertnet_token')}` },
        body: JSON.stringify({ expert_ids: selectedExperts }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || "Failed to log LinkedIn outreach", "error"); return; }
      showToast(data.message || "LinkedIn outreach logged");
      setLastLinkedinOutreachTime(Date.now());
      fetchProject();
    } catch (e) {
      showToast("Failed to log LinkedIn outreach", "error");
    }
  };

  const experts = projectData?.experts || [];
  const STATUS_ORDER = { confirmed: 0, engaged: 1, pitched: 2, pending: 3, no_interest: 4 };
  const LI_ORDER = (s) => (s === 'connected' || s === 'messaged' || s === 'follow_up_sent') ? 0 : 1;
  const sortedExperts = [...experts].sort((a, b) => {
    const statusDiff = (STATUS_ORDER[a.status] ?? 5) - (STATUS_ORDER[b.status] ?? 5);
    if (statusDiff !== 0) return statusDiff;
    const liDiff = LI_ORDER(a.linkedin_status) - LI_ORDER(b.linkedin_status);
    if (liDiff !== 0) return liDiff;
    const tsA = a.last_activity || a.last_engaged_at || a.added_at || '';
    const tsB = b.last_activity || b.last_engaged_at || b.added_at || '';
    return tsB < tsA ? -1 : tsB > tsA ? 1 : 0;
  });

  const statusStyle = {
    pending:     { background: 'rgba(138,163,181,0.12)', border: '1px solid rgba(138,163,181,0.25)', color: '#8aa3b5' },
    pitched:     { background: 'rgba(196,154,69,0.12)',  border: '1px solid rgba(196,154,69,0.28)',  color: '#c49a45' },
    engaged:     { background: 'rgba(69,196,154,0.12)',  border: '1px solid rgba(69,196,154,0.28)',  color: '#45c49a' },
    confirmed:   { background: 'rgba(90,180,204,0.12)',  border: '1px solid rgba(90,180,204,0.28)',  color: '#5ab4cc' },
    no_interest: { background: 'rgba(224,112,80,0.12)',  border: '1px solid rgba(224,112,80,0.28)',  color: '#e07050' },
  };

  const STATUS_COLOR = {
    pending: 'var(--st-pending)',
    pitched: 'var(--st-pitched)',
    engaged: 'var(--st-engaged)',
    confirmed: 'var(--st-confirmed)',
    no_interest: 'var(--st-no-interest)',
  };

  const priorityEntry = (p) => {
    if (p === 'high') return { label: 'High priority', color: '#e06a5a' };
    if (p === 'medium') return { label: 'Active', color: '#2dd4bf' };
    if (p === 'low') return { label: 'Long term', color: '#0e7490' };
    if (p === '' || p === null || p === undefined) return { label: 'On hold', color: '#4a6878' };
    return null;
  };

  const selectedWithoutEmail = experts.filter(e => selectedExperts.includes(e.id) && !e.email);
  const enrichNeeded = selectedWithoutEmail.length * enrichCreditPrice;
  const enrichShort = enrichCreditsShort || (enrichCreditBalance !== null && enrichCreditBalance < enrichNeeded);

  return (
    <>
      {enriching && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div className="r-card" style={{ padding: "28px 36px", textAlign: "center", color: "var(--text-1)" }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: 'rasp-pulse 2.4s ease-in-out infinite', margin: '0 auto 12px' }} />
            <div style={{ fontSize: 16, fontWeight: 600 }}>Enriching experts...</div>
            <div style={{ fontSize: 13, color: "var(--text-3)", marginTop: 6 }}>This may take a minute.</div>
          </div>
        </div>
      )}

      {salesNavImporting && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div className="r-card" style={{ padding: "28px 36px", textAlign: "center", color: "var(--text-1)" }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: 'rasp-pulse 2.4s ease-in-out infinite', margin: '0 auto 12px' }} />
            <div style={{ fontSize: 16, fontWeight: 600 }}>Scraping in progress...</div>
            <div style={{ fontSize: 13, color: "var(--text-3)", marginTop: 6 }}>Scraping your Sales Nav list. This may take 1–2 minutes.</div>
          </div>
        </div>
      )}

      <div style={{ padding: '24px', maxWidth: '1440px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '18px' }}>

        {/* Back button */}
        <button
          style={{ background: 'none', border: 'none', color: 'var(--text-3)', fontSize: '12px', cursor: 'pointer', alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--text-1)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-3)'}
          onClick={onBack}>
          ‹ Back to dashboard
        </button>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              {projectData?.ticker && (
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--accent)', border: '1px solid rgba(45,212,191,.35)', borderRadius: 4, padding: '2px 6px' }}>{projectData.ticker}</span>
              )}
              <h1 style={{ fontSize: '22px', fontWeight: '600', color: 'var(--text-1)', letterSpacing: '-0.01em' }}>{projectData?.name}</h1>
              {(() => { const e = priorityEntry(projectData?.priority); return e ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span className="r-dot" style={{ color: e.color }} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: e.color }}>{e.label}</span>
                </span>
              ) : null; })()}
              {projectData?.client && (
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Client {projectData.client}</span>
              )}
            </div>
            {projectData?.description && (
              <p style={{ fontSize: '13px', color: 'var(--text-3)', maxWidth: 760 }}>{projectData.description}</p>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto)', gap: 8, flexShrink: 0 }}>
            {selectedExperts.length > 0 && enrichNeeded > 0 && (
              <>
                <div data-testid="enrich-credits-preview" style={{ gridColumn: '1 / span 2', gridRow: 0, fontSize: 11, color: 'var(--text-3)', textAlign: 'right', paddingBottom: 2 }}>
                  Up to {enrichNeeded.toLocaleString()} credits · you have {enrichCreditBalance !== null ? enrichCreditBalance.toLocaleString() : '…'}
                </div>
                {enrichShort && (
                  <div data-testid="enrich-credits-short" style={{ gridColumn: '1 / span 2', gridRow: 0, fontSize: 11, color: 'var(--amber, #f59e0b)', textAlign: 'right', paddingBottom: 2 }}>
                    Not enough credits. Ask your admin for more.
                  </div>
                )}
              </>
            )}
            <button className="r-btn r-btn-ghost" style={{ height: 32, gridColumn: 1, gridRow: 1, opacity: (selectedExperts.length === 0 || (enrichNeeded > 0 && enrichShort)) ? 0.5 : 1, cursor: (selectedExperts.length === 0 || (enrichNeeded > 0 && enrichShort)) ? 'not-allowed' : 'pointer' }} onClick={enrichAll} disabled={selectedExperts.length === 0 || (enrichNeeded > 0 && enrichShort)}>
              Enrich{selectedExperts.length > 0 ? ` (${selectedExperts.length})` : ""}
            </button>
            {enrichPending > 0 ? (
              <div data-testid="enrich-pending-pill" style={{ height: 32, gridColumn: 1, gridRow: 2, display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', borderRadius: 8, border: '1px solid var(--line)', color: 'var(--text-3)', fontSize: 12 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', animation: 'rasp-pulse 2.4s ease-in-out infinite', flexShrink: 0 }} />
                Enriching {enrichPending} expert{enrichPending === 1 ? '' : 's'}… emails will appear here
              </div>
            ) : (
              <div style={{ gridColumn: 1, gridRow: 2 }} />
            )}
            <button className="r-btn r-btn-amber" style={{ height: 32, gridColumn: 2, gridRow: 1, opacity: (selectedExperts.length === 0 || sendingPitch) ? 0.5 : 1, cursor: (selectedExperts.length === 0 || sendingPitch) ? 'not-allowed' : 'pointer' }} onClick={sendEmails} disabled={selectedExperts.length === 0 || sendingPitch}>
              {sendingPitch ? "Sending…" : `Send Pitch${selectedExperts.length > 0 ? ` (${selectedExperts.length})` : ""}`}
            </button>
            <button className="r-btn r-btn-amber" style={{ height: 32, gridColumn: 2, gridRow: 2, opacity: (selectedExperts.length === 0 || sendingFollowup) ? 0.5 : 1, cursor: (selectedExperts.length === 0 || sendingFollowup) ? 'not-allowed' : 'pointer' }} onClick={sendLinkedInFollowups} disabled={selectedExperts.length === 0 || sendingFollowup}>
              {sendingFollowup ? "Sending…" : `Send Follow-Up${selectedExperts.length > 0 ? ` (${selectedExperts.length})` : ""}`}
            </button>
            <button className="r-btn r-btn-tint" style={{ height: 32, gridColumn: 3, gridRow: 1, opacity: selectedExperts.length === 0 ? 0.5 : 1, cursor: selectedExperts.length === 0 ? 'not-allowed' : 'pointer' }} onClick={logLinkedinOutreach} disabled={selectedExperts.length === 0}>
              Mark LinkedIn Outreach{selectedExperts.length > 0 ? ` (${selectedExperts.length})` : ""}
            </button>
            <button className="r-btn r-btn-ghost" style={{ height: 32, gridColumn: 4, gridRow: 1 }} onClick={() => setShowSettings(s => !s)}>Settings</button>
          </div>
        </div>

        {showSettings && (
          <div className="r-card" style={{ padding: 18 }}>
            <div className="r-eyebrow" style={{ color: 'var(--accent)', marginBottom: 16, borderBottom: '1px solid var(--line-soft)', paddingBottom: 8 }}>Project Details</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <div className="form-group">
                <label className="form-label">Project Name</label>
                <input className="form-input" value={projectDetails.name}
                  onChange={(e) => setProjectDetails({ ...projectDetails, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">TICKER</label>
                <input className="form-input" placeholder="e.g. AAPL" value={projectDetails.ticker}
                  onChange={(e) => setProjectDetails({ ...projectDetails, ticker: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Client</label>
                <input className="form-input" value={projectDetails.client}
                  onChange={(e) => setProjectDetails({ ...projectDetails, client: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Brief / Description</label>
              <textarea className="form-input" rows={2} value={projectDetails.description}
                onChange={(e) => setProjectDetails({ ...projectDetails, description: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[{ val: "high", label: "High Priority", color: "#e06a5a" }, { val: "medium", label: "Medium", color: "#2dd4bf" }, { val: "low", label: "Long-Term", color: "#0e7490" }].map((opt) => (
                  <button key={opt.val} onClick={() => setProjectDetails({ ...projectDetails, priority: opt.val })}
                    style={{ flex: 1, height: 32, borderRadius: 8, border: projectDetails.priority === opt.val ? `1px solid ${opt.color}` : "1px solid var(--line)", background: projectDetails.priority === opt.val ? `${opt.color}1f` : "transparent", color: projectDetails.priority === opt.val ? opt.color : "var(--text-3)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Pitch Template</label>
              <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 6 }}>Use <code style={{ background: 'var(--input-bg)', padding: "1px 4px", borderRadius: 3 }}>[Name]</code> to insert the recipient's first name (any capitalization works). Other placeholders are not supported yet — write the rest as plain text.</p>
              <textarea className="form-input" rows={6} value={projectDetails.pitch_template}
                onChange={(e) => setProjectDetails({ ...projectDetails, pitch_template: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Subject Line</label>
              <input className="form-input" value={projectDetails.subject_line}
                onChange={(e) => setProjectDetails({ ...projectDetails, subject_line: e.target.value })} />
            </div>
            <div className="form-group" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <label className="form-label" style={{ margin: 0 }}>Automatic Follow-ups</label>
              <div
                onClick={() => setProjectDetails(d => ({ ...d, auto_followups: d.auto_followups ? 0 : 1 }))}
                style={{ width: 40, height: 22, borderRadius: 11, background: projectDetails.auto_followups ? "rgba(45,212,191,.6)" : "rgba(255,255,255,.12)", position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}
              >
                <div style={{ position: "absolute", top: 3, left: projectDetails.auto_followups ? 21 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
              </div>
            </div>
            <div className="r-eyebrow" style={{ color: 'var(--accent)', margin: "20px 0 16px", borderBottom: '1px solid var(--line-soft)', paddingBottom: 8 }}>Outreach Settings</div>
            <div className="form-group">
              <label className="form-label">Follow-up Message</label>
              <textarea className="form-input" rows={4}
                value={projectDetails.follow_up_message}
                onChange={(e) => setProjectDetails({ ...projectDetails, follow_up_message: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">LinkedIn Follow-up Message</label>
              <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 6 }}>
                Sent via Sales Navigator to experts who were previously pitched on LinkedIn. Use {'{first_name}'} to personalize.
              </p>
              <textarea
                className="form-input"
                rows={5}
                value={projectDetails.linkedin_followup_template || ''}
                onChange={(e) => setProjectDetails({ ...projectDetails, linkedin_followup_template: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Follow-up Delay (days)</label>
              <input className="form-input" style={{ width: 80 }}
                value={settings.follow_up_delay_days}
                onChange={(e) => setSettings({ ...settings, follow_up_delay_days: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">LinkedIn Batch Delay (minutes)</label>
              <input className="form-input" style={{ width: 80 }}
                value={settings.linkedin_batch_delay_minutes}
                onChange={(e) => setSettings({ ...settings, linkedin_batch_delay_minutes: e.target.value })} />
            </div>
            <button className="btn btn-primary" onClick={saveSettings}>Save Settings</button>
          </div>
        )}

        {sources.length > 1 && (
          <div style={{ display: 'flex', gap: 0 }}>
            {sources.map(source => (
              <button
                key={source.id}
                onClick={() => setActiveSourceId(source.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  borderBottom: activeSourceId === source.id ? '2px solid var(--accent)' : '2px solid transparent',
                  color: activeSourceId === source.id ? 'var(--text-1)' : 'var(--text-3)',
                  fontSize: 13,
                  fontWeight: activeSourceId === source.id ? 500 : 400,
                  cursor: 'pointer',
                  padding: '4px 12px',
                  marginBottom: -1,
                }}
              >
                {source.name}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: 'space-between', borderBottom: "1px solid var(--line)" }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {["experts", "emails", "notes"].map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{
                background: "none", border: "none", padding: "10px 16px", cursor: "pointer",
                fontSize: 13, fontWeight: 500,
                color: activeTab === tab ? 'var(--text-1)' : 'var(--text-3)',
                borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: -1,
              }}>
                {tab === "experts" ? (
                  <span>Experts <span className="r-mono" style={{ color: 'var(--text-4)' }}>{sortedExperts.length}</span></span>
                ) : tab === "emails" ? (
                  <span>Emails <span className="r-mono" style={{ color: 'var(--text-4)' }}>{(projectData?.experts || []).filter(e => e.email_status).length}</span></span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    Notes
                    {notesUnreadCount > 0 && (
                      <span style={{ background: 'var(--negative)', color: '#fff', borderRadius: '999px', fontFamily: 'var(--font-data)', fontSize: 10, fontWeight: 600, minWidth: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1 }}>
                        {notesUnreadCount}
                      </span>
                    )}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button className="r-btn r-btn-ghost" style={{ height: 30 }} onClick={() => setShowAddExpert(true)}>+ Add expert</button>
        </div>

        {activeTab === "notes" ? (
          <div>
            {notesLoading ? (
              <div style={{ color: "var(--text-3)", textAlign: "center", padding: "40px 20px", fontSize: 13 }}>Loading...</div>
            ) : projectNotes.length === 0 ? (
              <div style={{ color: "var(--text-3)", textAlign: "center", padding: "40px 20px", fontSize: 13 }}>No notes from managers yet.</div>
            ) : projectNotes.map(note => (
              <div key={note.id} className="r-card" style={{ padding: 16, marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--accent)", fontSize: 12, fontWeight: 600 }}>{note.manager_username}</span>
                  <span style={{ fontFamily: 'var(--font-data)', color: "var(--text-4)", fontSize: 11 }}>{new Date(note.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                </div>
                <div style={{ color: "var(--text-2)", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>{note.note_text}</div>
              </div>
            ))}
          </div>
        ) : activeTab === "emails" ? (
          <div className="r-card">
            <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 3fr 1fr 1fr", padding: "0 18px", height: 34, background: "var(--subbar-bg)", borderBottom: "1px solid var(--line)", alignItems: 'center', fontFamily: 'var(--font-data)', fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--text-4)" }}>
              <span>Name</span><span>Email</span><span>Subject</span><span>Status</span><span>Date Sent</span>
            </div>
            {(projectData?.experts || []).filter(e => e.email_status).length === 0 ? (
              <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>No emails sent yet.</div>
            ) : (projectData?.experts || []).filter(e => e.email_status).map((expert, idx) => (
              <div key={expert.id} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 3fr 1fr 1fr", padding: "0 18px", minHeight: 52, borderBottom: "1px solid var(--line-soft)", alignItems: "center", background: idx % 2 === 1 ? 'var(--row-odd)' : 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 1 ? 'var(--row-odd)' : 'transparent'}>
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)" }}>{expert.name}</span>
                <span style={{ fontFamily: 'var(--font-data)', color: "var(--text-2)", fontSize: 12 }}>{expert.email || "—"}</span>
                <span style={{ color: "var(--text-2)", fontSize: 12 }}>{projectData?.subject_line || "—"}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span className="r-dot" style={{ color: STATUS_COLOR[expert.email_status] || STATUS_COLOR.pending }} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: STATUS_COLOR[expert.email_status] || STATUS_COLOR.pending, textTransform: 'capitalize' }}>{expert.email_status}</span>
                </span>
                <span style={{ fontFamily: 'var(--font-data)', color: "var(--text-3)", fontSize: 12 }}>Recently</span>
              </div>
            ))}
          </div>
        ) : sortedExperts.length === 0 ? (
          <div className="empty-state"><p>No experts yet. Import a Sales Nav CSV.</p></div>
        ) : (
          <div className="r-card">
            <div style={{ display: 'grid', gridTemplateColumns: '36px minmax(200px,1.4fr) 1.3fr 1fr 1.4fr 36px 110px 90px 32px', height: 34, padding: '0 18px', background: 'var(--subbar-bg)', borderBottom: '1px solid var(--line)', alignItems: 'center', fontFamily: 'var(--font-data)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-4)' }}>
              <span>
                <input type="checkbox"
                  style={{ accentColor: 'var(--accent)' }}
                  checked={sortedExperts.length > 0 && selectedExperts.length === sortedExperts.length}
                  onChange={(e) => setSelectedExperts(e.target.checked ? sortedExperts.map(x => x.id) : [])} />
              </span>
              <span>Expert</span>
              <span>Current Role</span>
              <span>Location</span>
              <span>Email</span>
              <span>LI</span>
              <span>Status</span>
              <span>Last Activity</span>
              <span></span>
            </div>
            {sortedExperts.map((expert, idx) => {
              return (
                <div key={expert.id} style={{ display: 'grid', gridTemplateColumns: '36px minmax(200px,1.4fr) 1.3fr 1fr 1.4fr 36px 110px 90px 32px', minHeight: 52, padding: '0 18px', borderBottom: '1px solid var(--line-soft)', alignItems: 'center', background: idx % 2 === 1 ? 'var(--row-odd)' : 'transparent' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--row-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 1 ? 'var(--row-odd)' : 'transparent'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingExpert(expert); setExpertEditForm({ name: expert.name, title: expert.title || '', location: expert.location || '', email: expert.email || '' }); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'inline-flex', alignItems: 'center', color: 'var(--text-4)', fontSize: 12 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--text-2)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}
                    >⚙</button>
                    <input type="checkbox"
                      style={{ accentColor: 'var(--accent)' }}
                      checked={selectedExperts.includes(expert.id)}
                      onChange={(e) => setSelectedExperts(e.target.checked
                        ? [...selectedExperts, expert.id]
                        : selectedExperts.filter(id => id !== expert.id))} />
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span
                        style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)', cursor: expert.sales_nav_url ? 'pointer' : 'default' }}
                        onClick={() => { if (expert.sales_nav_url) window.open(expert.sales_nav_url, '_blank'); }}
                      >{expert.name}</span>
                      {expert.sales_nav_url ? (
                        <button
                          onClick={e => { e.stopPropagation(); window.open(expert.sales_nav_url, '_blank'); }}
                          style={{ fontFamily: 'var(--font-data)', fontSize: '10px', lineHeight: '16px', padding: '0 6px', borderRadius: 4, border: '1px solid rgba(45,212,191,.28)', background: 'rgba(45,212,191,.08)', color: 'var(--accent)', cursor: 'pointer' }}
                        >
                          <img src={compassIcon} alt="" style={{ width: 11, height: 11, verticalAlign: 'middle', marginRight: 3 }} /> Sales Nav
                        </button>
                      ) : expert.linkedin_url ? (
                        <button
                          onClick={e => { e.stopPropagation(); window.open(expert.linkedin_url, '_blank'); }}
                          style={{ fontFamily: 'var(--font-data)', fontSize: '10px', lineHeight: '16px', padding: '0 6px', borderRadius: 4, border: '1px solid rgba(45,212,191,.28)', background: 'rgba(45,212,191,.08)', color: 'var(--accent)', cursor: 'pointer' }}
                        >LinkedIn</button>
                      ) : null}
                      {(expert.open_profile === 1 || expert.open_profile === true) && (
                        <span style={{ fontFamily: 'var(--font-data)', fontSize: 10, lineHeight: '16px', padding: '0 6px', borderRadius: 4, border: '1px solid rgba(69,196,154,.28)', background: 'rgba(69,196,154,.10)', color: 'var(--positive)' }}>Open</span>
                      )}
                      {(expert.premium === 1 || expert.premium === true) && (
                        <span style={{ fontFamily: 'var(--font-data)', fontSize: 10, lineHeight: '16px', padding: '0 6px', borderRadius: 4, border: '1px solid rgba(224,160,80,.35)', background: 'rgba(224,160,80,.10)', color: '#e0a050' }}>Premium</span>
                      )}
                      {expert.inmail_status === "sent" && (
                        <span style={{ fontFamily: 'var(--font-data)', fontSize: 10, lineHeight: '16px', padding: '0 6px', borderRadius: 4, border: '1px solid rgba(160,120,240,.32)', background: 'rgba(160,120,240,.10)', color: '#b48cf2' }}>InMail</span>
                      )}
                      {expert.linkedin_status === "messaged" && (
                        <span style={{ fontFamily: 'var(--font-data)', fontSize: 10, lineHeight: '16px', padding: '0 6px', borderRadius: 4, border: '1px solid rgba(69,196,154,.28)', background: 'rgba(69,196,154,.10)', color: 'var(--positive)' }}>LI Messaged</span>
                      )}
                      {expert.linkedin_status === "follow_up_sent" && (
                        <span style={{ fontFamily: 'var(--font-data)', fontSize: 10, lineHeight: '16px', padding: '0 6px', borderRadius: 4, border: '1px solid rgba(160,120,240,.32)', background: 'rgba(160,120,240,.10)', color: '#b48cf2' }}>LI Follow-up</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{expert.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{expert.company}</div>
                  </div>
                  <div style={{ fontSize: 12, color: expert.location ? 'var(--text-3)' : 'var(--text-5)' }}>
                    {expert.location || '—'}
                  </div>
                  <div>
                    {expert.email
                      ? <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-2)' }}>{expert.email}</span>
                      : editingEmailId === expert.id
                        ? <input
                            autoFocus
                            placeholder="Enter email..."
                            style={{ fontSize: 12, padding: "2px 6px", borderRadius: 4, border: "1px solid var(--rasp-teal-edge)", background: "var(--rasp-inset)", color: "var(--rasp-fg-1)", width: 160 }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.currentTarget.dataset.handled = "1"; saveEmail(expert.id, e.currentTarget.value.trim()); }
                              else if (e.key === "Escape") { e.currentTarget.dataset.handled = "1"; setEditingEmailId(null); }
                            }}
                            onBlur={(e) => { if (!e.currentTarget.dataset.handled) saveEmail(expert.id, e.currentTarget.value.trim()); }}
                          />
                        : <span onClick={() => setEditingEmailId(expert.id)} style={{ cursor: "pointer", fontFamily: 'var(--font-data)', fontSize: 10, lineHeight: '16px', padding: '0 6px', borderRadius: 4, border: '1px solid rgba(224,160,80,.35)', background: 'rgba(224,160,80,.10)', color: '#e0a050' }}>No Email</span>}
                    <div style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)', marginTop: 2 }}>
                      Email {expert.email_message_count || 0} · LI {expert.linkedin_message_count || 0}
                    </div>
                  </div>
                  <div>
                    {expert.linkedin_status === 'connected' ? (
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 8px rgba(45,212,191,.6)' }} />
                    ) : (
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', border: '1px solid var(--text-5)' }} />
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span className="r-dot" style={{ color: STATUS_COLOR[expert.status] || STATUS_COLOR.pending }} />
                    {false ? (
                      <span style={{
                        ...statusStyle.pending, fontSize: 11, fontWeight: 600,
                        padding: '3px 7px', borderRadius: 6, display: 'inline-block',
                      }}>Pending</span>
                    ) : (
                      <select
                        value={expert.status === 'confirmed' || expert.status === 'no_interest' || expert.status === 'engaged' || expert.status === 'pending' ? expert.status : ''}
                        onChange={(e) => updateStatus(expert.pe_id, e.target.value)}
                        style={{
                          background: 'none', border: 'none',
                          color: STATUS_COLOR[expert.status] || STATUS_COLOR.pending,
                          fontSize: 12, fontWeight: 500, padding: 0,
                          cursor: "pointer", outline: "none", appearance: "none",
                          WebkitAppearance: "none",
                        }}
                      >
                        {expert.status === 'pitched' && (
                          <option value="" disabled>Pitched</option>
                        )}
                        <option value="pending">Pending</option>
                        <option value="engaged">Engaged</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="no_interest">No Interest</option>
                      </select>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {(() => {
                      const timestamps = [
                        expert.outreach_sent_at,
                        expert.linkedin_messaged_at,
                        expert.linkedin_follow_up_sent_at,
                        expert.last_engaged_at
                      ].filter(Boolean);
                      if (!timestamps.length) return <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-5)' }}>—</span>;
                      const latest = new Date(Math.max(...timestamps.map(t => new Date(t).getTime())));
                      const diff = (Date.now() - latest.getTime()) / 1000;
                      let ago;
                      if (diff < 60) ago = 'just now';
                      else if (diff < 3600) ago = `${Math.floor(diff / 60)}m ago`;
                      else if (diff < 86400) ago = `${Math.floor(diff / 3600)}h ago`;
                      else ago = `${Math.floor(diff / 86400)}d ago`;
                      return <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-3)' }}>{ago}</span>;
                    })()}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setOpenMenuExpertId(openMenuExpertId === expert.id ? null : expert.id); }}
                      style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', fontSize: 16, width: 24, height: 24, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                    >⋯</button>
                    {openMenuExpertId === expert.id && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ position: 'absolute', right: 0, top: '100%', zIndex: 100, background: 'var(--glass-card)', border: '1px solid var(--line)', borderRadius: 8, boxShadow: 'var(--card-shadow)', minWidth: 170, overflow: 'hidden' }}
                      >
                        <div
                          onClick={() => sendToScheduling(expert, 'schedule')}
                          style={{ padding: '8px 14px', fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >Send to Schedule</div>
                        <div
                          onClick={() => sendToScheduling(expert, 'qualifying')}
                          style={{ padding: '8px 14px', fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >Send to Qualifying</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div style={{ height: 40, padding: '0 18px', borderTop: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-3)' }}>
              <span>{sortedExperts.length} experts</span>
              {selectedExperts.length > 0 && (
                <span style={{ fontFamily: 'var(--font-data)' }}>{selectedExperts.length} selected</span>
              )}
            </div>
          </div>
        )}
      </div>

      {showSalesNav && (
        <div className="modal-overlay" onClick={() => setShowSalesNav(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Import from Sales Nav</h2>
            <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 12 }}>
              Paste your Sales Navigator list URL to scrape and import results automatically.
            </p>
            <label className="form-label">Sales Navigator List URL</label>
            <input className="form-input" type="text" placeholder="https://www.linkedin.com/sales/lists/..."
              value={salesNavUrl} onChange={(e) => setSalesNavUrl(e.target.value)} />
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowSalesNav(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={startSalesNavImport}>Start Import</button>
            </div>
          </div>
        </div>
      )}

      {editingExpert && (
        <div className="modal-overlay" onClick={() => setEditingExpert(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Expert</h2>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" value={expertEditForm.name}
                onChange={(e) => setExpertEditForm({ ...expertEditForm, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Current Role</label>
              <input className="form-input" value={expertEditForm.title}
                onChange={(e) => setExpertEditForm({ ...expertEditForm, title: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-input" value={expertEditForm.location}
                onChange={(e) => setExpertEditForm({ ...expertEditForm, location: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" value={expertEditForm.email}
                onChange={(e) => setExpertEditForm({ ...expertEditForm, email: e.target.value })} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditingExpert(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveExpertEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showAddExpert && (
        <div className="modal-overlay" onClick={() => setShowAddExpert(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add Expert</h2>
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input className="form-input" placeholder="Full name" value={addExpertForm.name}
                onChange={(e) => setAddExpertForm({ ...addExpertForm, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Current Role</label>
              <input className="form-input" placeholder="e.g. VP Strategy at Acme Corp" value={addExpertForm.title}
                onChange={(e) => setAddExpertForm({ ...addExpertForm, title: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-input" placeholder="e.g. New York, NY" value={addExpertForm.location}
                onChange={(e) => setAddExpertForm({ ...addExpertForm, location: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" placeholder="email@example.com" value={addExpertForm.email}
                onChange={(e) => setAddExpertForm({ ...addExpertForm, email: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Sales Navigator URL</label>
              <input className="form-input" placeholder="https://www.linkedin.com/sales/people/..." value={addExpertForm.sales_nav_url}
                onChange={(e) => setAddExpertForm({ ...addExpertForm, sales_nav_url: e.target.value })} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowAddExpert(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addExpertManually}>Add Expert</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={"toast " + toast.type}><span className="r-dot" style={{ color: toast.type === 'error' ? 'var(--negative)' : 'var(--positive)' }} />{toast.msg}</div>
      )}
    </>
  );
}
